import { CDN_URL, WORKER_URL, CREATE_SPRITE_WORKER_URL, DELAY_ESTIMATION_URL, catalogJsonUrl } from '../config.js';
import { initSearchDropdown } from './ui/searchDropdown.js';
import { storage } from '../utils/storage.js';
import {
  getUserCredits,
  getAuthToken,
  showFreeAccountModalIfNeeded,
  syncSpriteCredits,
  upgradeVideoResolutionRequest,
  waitForSpriteCompletion,
  waitForVideoCompletion,
} from './videoScheduleApi.js';
import {
  loadCreditPricing,
  computeImageBatchTotal,
  computeImageUnitCost,
  computeVideoUnitCost,
  getCharacterHasLora,
  getCharacterStyle,
  buildLocalCreditResult,
  interpolateCreditTemplate,
} from './creditCostApi.js';
import { mountVideoLoraRows, SPRITE_VIDEO_LORA_MAX } from './videoLoraRows.js';
import { initializePromptAutocomplete, initializeOutfitPromptAutocomplete, initializeNegativePromptAutocomplete } from './promptAutocomplete.js';
import { initSfwLoraLock } from './sfwLoraLock.js';
import { syncRichPromptEditorDisabledState } from './richPromptEditor.js';
import { addSwipeSupport } from '../../core/utils/swipe-handlers.js';
import { fetchCache, FetchCache } from '../utils/fetchCache.js';
import { withSfwQueryString } from '../utils/nsfwTags.js';
import { getBlockedFilteredImagePromptMessage, getBlockedUnrecognizedPromptMessage } from './danbooruTags.js';
import { getNsfwEnabled } from '../utils/userPrefs.js';
import { showSuccess, showError, showInfo } from '../utils/UnifiedNotificationSystem.js';
import { IMAGE_KINDS, MODES, VIDEO_KINDS } from './videoPage/parseCreateVideoConfig.js';
import { initFreeVideoFlow } from './videoPage/freeVideoFlow.js';
import { initFreeImageFlow } from './videoPage/freeImageFlow.js';
import { pollCreateVideoJobs } from './videoPage/pollCreateVideoJobs.js';
import { notifyVideoContainerStarting } from './waitForQueuedVideo.js';
import { setPreviewCardState } from './videoPage/previewCardState.js';
import { attachSpriteContextMenuListeners } from '../hangout/ui/SpriteContextMenu.js';
import { parseSpriteUrl } from '../hangout/ui/services/spriteUpgradeService.js';
import { attachCreateVideoRankedCharacterPickerToTrigger } from './ui/rankedCharacterPickerModal.js';
import { interpolate, resolveCreateCommonText, resolveImageGenError } from '../translations.js';
import { sfwModerationMessage } from './sfwModerationError.js';
import { openFieldEditModal } from './fieldEditModal.js';
import { fetchCharacterVideos, getVideoBadgeText, bindSpriteGalleryNameFilter } from '../utils/spriteVideoStatus.js';
import { fetchPrivateCharacters } from '../utils/privateCharactersApi.js';
import { postGenerate } from './imageGeneration.js';
import { requestAiGenerateField } from './generationApi.js';
import { normalizeGender } from '../utils/genderUtils.js';
import { FEMALE_STANDING_ACTION_IDS, FEMALE_DEFAULT_STANDING } from '../utils/spriteFallbacks.js';
import { mapCreateCharacters, sortCharactersByRankAsc } from './utils/mapCreateCharacters.js';
import { unwrapOutfitsPayload } from './utils/unwrapOutfitsPayload.js';
import { filterOutfitsByGender, sortOutfitsByIdAsc } from './utils/filterOutfitsByGender.js';
import {
  getSelectableOptions,
  cycleSelect,
  ensureSelectHasDefault,
  isSearchDropdownOpen,
} from './utils/selectCycle.js';
import { initPromptHelpers } from './utils/promptHelpersUi.js';
import {
  spriteKey,
  getSpriteSmallImagePath,
  formatSpriteNameForDisplay,
  formatSpriteNameForPublish,
} from './utils/spriteDisplay.js';

const getCreateVideoT = (key, fallback) => {
  const lang = window.getCurrentLanguage ? window.getCurrentLanguage() : 'en';
  const t = window.translations?.[lang]?.['create-video'] ?? window.translations?.en?.['create-video'];
  return t?.[key] ?? fallback;
};


const hasOutfitId = (value) => value !== '' && value !== undefined && value !== null;

const refreshOutfitSearch = (selectId) => {
  const placeholder = getCreateVideoT('select_outfit', 'Select an outfit');
  const config = { placeholder, searchLabel: placeholder, searchPlaceholder: placeholder };
  const existing = window.searchDropdownRegistry?.[selectId];
  if (existing?.refresh) {
    existing.refresh(config);
    return;
  }
  initSearchDropdown(selectId, config);
};

let sseToastUntil = 0;
const noteSseToast = () => {
  sseToastUntil = Date.now() + 20000;
};
const shouldSuppressJobToast = () => Date.now() < sseToastUntil;

const showNotification = (message, type = 'info') => {
  if (type === 'success') showSuccess(message);
  else if (type === 'error') showError(message);
  else showInfo(message);
};

const fetchCharacterOutfitIds = async (characterId) => {
  if (!characterId) return [];
  try {
    const ids = await fetchCache.fetchJSON(`${WORKER_URL}/character/outfits?character_id=${encodeURIComponent(characterId)}`);
    if (!Array.isArray(ids)) return [];
    return ids.map((id) => String(id));
  } catch {
    return [];
  }
};

const state = {
  mode: MODES.SPRITE_TEXT,
  characters: [],
  outfits: [],
  actionMappings: {},
  tool1: {
    receiverSprites: [],
    selectedKeys: new Set(),
    forcedKeys: new Set(),
  },
  tool3: {
    customFlowEnabled: false,
    spriteFlow: 'outfit',
    preview: {
      imageUrl: null,
      generatedFor: null,
    },
  },
  tool4: {
    sprites: [],
    videos: [],
    selectedSpriteKey: null,
    selectMode: 'create',
    pendingSelectActionId: null,
    pendingSelectIntent: null,
    pendingSelectCharId: null,
    pendingSelectOutfitId: null,
  },
};

const resolveMappedActionName = (actionId, sprite) => {
  const mapping = state.actionMappings[actionId] ?? state.actionMappings[String(actionId)];
  const mappedName = typeof mapping?.name === 'string' ? mapping.name.trim() : '';
  if (mappedName) return mappedName;
  const fallback = sprite?.action_name || sprite?.actionName || '';
  return typeof fallback === 'string' ? fallback.trim() : '';
};

const fetchGridSprites = async (characterId, outfitId) => {
  if (!characterId || !hasOutfitId(outfitId)) return [];
  try {
    const url = withSfwQueryString(`${WORKER_URL}/get_grid_sprites?character_id=${characterId}&outfit_id=${outfitId}`);
    const fetchOptions = {};
    const authToken = await getAuthToken();
    if (authToken) fetchOptions.headers = { Authorization: `Bearer ${authToken}` };
    const data = await fetchCache.fetchJSON(url, fetchOptions);
    return (data.sprites || []).map((sprite) => ({
      ...sprite,
      actionName: resolveMappedActionName(sprite.action_id, sprite),
    }));
  } catch (error) {
    console.error('[CreateVideo] Failed to fetch grid sprites:', error);
    return [];
  }
};

const queryElements = () => ({
  modeTabs: document.querySelectorAll('[data-tablist="creation-mode"] .create-mode-tab'),
  spriteFlowTabs: document.querySelectorAll('[data-tablist="sprite-flow"] .create-mode-tab'),
  panels: document.querySelectorAll('.create-tool-panel'),
  spriteSectionRef: document.getElementById('sprite-section-ref'),
  spriteGridRef: document.getElementById('sprite-grid-ref'),
  spriteActionSearchRef: document.getElementById('sprite-action-search-ref'),
  spriteStatusRef: document.getElementById('sprite-status-ref'),
  creditInfoRef: document.getElementById('credit-info-ref'),
  validateSpriteRef: document.getElementById('validate-sprite-ref'),
  customPromptAdvancedRef: document.getElementById('sprite-custom-prompt-advanced'),
  customOutfitPromptRef: document.getElementById('sprite-custom-prompt-ref'),
  customOutfitNegativeRef: document.getElementById('sprite-custom-negative-ref'),
  customPromptLabelRef: document.getElementById('sprite-custom-prompt-label'),
  customPromptTierNoticeRef: document.getElementById('sprite-custom-prompt-tier-notice'),
  receiverChar3: document.getElementById('receiver-char-3'),
  receiverOutfit3: document.getElementById('receiver-outfit-3'),
  targetPreviewCard3: document.getElementById('target-preview-card-3'),
  targetPreviewArrowLeft3: document.getElementById('target-preview-arrow-left-3'),
  targetPreviewArrowRight3: document.getElementById('target-preview-arrow-right-3'),
  promptText3: document.getElementById('prompt-text-3'),
  spriteName3: document.getElementById('sprite-name-3'),
  spritePreviewSection3: document.getElementById('sprite-preview-section-3'),
  spritePreviewCard3: document.getElementById('sprite-preview-card-3'),
  spritePreviewStatus3: document.getElementById('sprite-preview-status-3'),
  discardSpritePreview3: document.getElementById('discard-sprite-preview-3'),
  publishSpritePreview3: document.getElementById('publish-sprite-preview-3'),
  customImageFlow3: document.getElementById('custom-image-flow-3'),
  promptHelper3: document.getElementById('prompt-helper-3'),
  spriteAdvancedLora3: document.getElementById('sprite-advanced-lora-3'),
  spriteLoraId3: document.getElementById('sprite-lora-id-3'),
  spriteLoraStrength3: document.getElementById('sprite-lora-strength-3'),
  creditInfoSpriteText: document.getElementById('credit-info-sprite-text'),
  validateSpriteText: document.getElementById('validate-sprite-text'),
  aiWandPrompt3: document.getElementById('ai-wand-prompt-3'),
  receiverChar4: document.getElementById('receiver-char-4'),
  receiverOutfit4: document.getElementById('receiver-outfit-4'),
  targetPreviewCard4: document.getElementById('target-preview-card-4'),
  targetPreviewArrowLeft4: document.getElementById('target-preview-arrow-left-4'),
  targetPreviewArrowRight4: document.getElementById('target-preview-arrow-right-4'),
  receiverCharFreeVideo: document.getElementById('receiver-char-free-video'),
  receiverOutfitFreeVideo: document.getElementById('receiver-outfit-free-video'),
  targetPreviewCardFreeVideo: document.getElementById('target-preview-card-free-video'),
  targetPreviewArrowLeftFreeVideo: document.getElementById('target-preview-arrow-left-free-video'),
  targetPreviewArrowRightFreeVideo: document.getElementById('target-preview-arrow-right-free-video'),
  spriteGridForVideo: document.getElementById('sprite-grid-for-video'),
  spriteActionSearchVideo: document.getElementById('sprite-action-search-video'),
  spriteStatusVideo: document.getElementById('sprite-status-video'),
  spriteSelectionSection4: document.getElementById('sprite-selection-section-4'),
  createVideoForm4: document.getElementById('create-video-form-4'),
  cameraText4: document.getElementById('camera-text-4'),
  videoAdvancedLora: document.getElementById('video-advanced-lora'),
  videoLoraTitle: document.getElementById('video-lora-title'),
  videoLoraRows: document.getElementById('video-lora-rows'),
  videoAddLora: document.getElementById('video-add-lora'),
  videoLoraLimit: document.getElementById('video-lora-limit'),
  creditInfoVideoText: document.getElementById('credit-info-video-text'),
  validateVideoText: document.getElementById('validate-video-text'),
  upgradeVideoForm4: document.getElementById('upgrade-video-form-4'),
  creditInfoUpgradeText: document.getElementById('credit-info-upgrade-text'),
  validateVideoUpgrade: document.getElementById('validate-video-upgrade'),
});

let elements = null;
let spriteVideoLoras = null;
const getElements = () => {
  if (!elements) elements = queryElements();
  return elements;
};

const spriteNameFilters = { ref: null, video: null };
const applySpriteNameFilters = () => {
  spriteNameFilters.ref?.();
  spriteNameFilters.video?.();
};

/** Char/outfit for the reference grid (Design an Image tool). */
const getSpriteRefReceiverIds = () => {
  const els = getElements();
  return {
    charId: els.receiverChar3?.value || '',
    outfitId: els.receiverOutfit3?.value,
  };
};

const SPRITE_FLOWS = {
  OUTFIT: 'outfit',
  CUSTOM: 'custom',
};

const setCustomImageFlowEnabled = (isEnabled) => {
  const els = getElements();
  const flowEl = els.customImageFlow3;
  if (!flowEl) return;

  state.tool3.customFlowEnabled = Boolean(isEnabled);
  flowEl.classList.toggle('custom-image-flow--disabled', !isEnabled);
  flowEl.setAttribute('aria-disabled', isEnabled ? 'false' : 'true');

  const bridgeEl = flowEl.querySelector('.custom-image-flow__bridge');
  if (bridgeEl) {
    bridgeEl.textContent = isEnabled
      ? getCreateVideoT('bridge_custom_image', 'Want to create a brand-new custom image instead? Continue below.')
      : getCreateVideoT('bridge_custom_image_locked', 'Create the outfit images above first, then you can make a custom image here.');
  }

  const controls = [
    els.promptText3,
    els.spriteName3,
    els.aiWandPrompt3,
    els.spriteLoraId3,
    els.spriteLoraStrength3,
    ...(els.promptHelper3?.querySelectorAll('.prompt-helper__button') || []),
  ];
  controls.forEach((el) => {
    if (!el) return;
    el.disabled = !isEnabled;
  });
  syncRichPromptEditorDisabledState(els.promptText3);

  if (!isEnabled) {
    clearTool3Preview();
    if (els.validateSpriteText) els.validateSpriteText.disabled = true;
    if (els.spriteAdvancedLora3) els.spriteAdvancedLora3.removeAttribute('open');
    if (els.spriteLoraId3) els.spriteLoraId3.value = '';
    if (els.spriteLoraStrength3) els.spriteLoraStrength3.value = '';
    return;
  }

  validateTool3Form();
};

const resolveGenderForCharacterId = (characterId) => {
  const char = state.characters.find((c) => Number(c.id) === Number(characterId));
  return normalizeGender(char);
};

const TOOL_PREVIEW_CONFIG = {
  3: {
    cardKey: 'targetPreviewCard3',
    charKey: 'receiverChar3',
    outfitKey: 'receiverOutfit3',
    leftArrowKey: 'targetPreviewArrowLeft3',
    rightArrowKey: 'targetPreviewArrowRight3',
    fullSize: false,
    outfitSelectId: 'receiver-outfit-3',
  },
  4: {
    cardKey: 'targetPreviewCard4',
    charKey: 'receiverChar4',
    outfitKey: 'receiverOutfit4',
    leftArrowKey: 'targetPreviewArrowLeft4',
    rightArrowKey: 'targetPreviewArrowRight4',
    fullSize: true,
    outfitSelectId: 'receiver-outfit-4',
  },
  freeVideo: {
    cardKey: 'targetPreviewCardFreeVideo',
    charKey: 'receiverCharFreeVideo',
    outfitKey: 'receiverOutfitFreeVideo',
    leftArrowKey: 'targetPreviewArrowLeftFreeVideo',
    rightArrowKey: 'targetPreviewArrowRightFreeVideo',
    fullSize: true,
    outfitSelectId: 'receiver-outfit-free-video',
  },
};

const resolveGenderForTool = (toolKey) => {
  const els = getElements();
  const config = TOOL_PREVIEW_CONFIG[toolKey];
  const charId = els[config?.charKey]?.value;
  return resolveGenderForCharacterId(charId);
};

const updateToolPreview = async (toolKey) => {
  const config = TOOL_PREVIEW_CONFIG[toolKey];
  if (!config) return;
  const els = getElements();
  const cardEl = els[config.cardKey];
  const charId = els[config.charKey]?.value;
  const outfitSelectEl = els[config.outfitKey];
  const outfitRaw = outfitSelectEl?.value;
  if (!charId || !hasOutfitId(outfitRaw)) {
    setPreviewCardState(cardEl, { placeholderText: 'Select a character and outfit to preview' });
    return;
  }
  const outfitId = Number(outfitRaw);

  const outfitOption = outfitSelectEl?.querySelector(`option[value="${outfitId}"]`);
  const isUnavailable = outfitOption?.hasAttribute('data-unavailable');
  const previewOutfitId = isUnavailable ? 0 : outfitId;

  setPreviewCardState(cardEl, { placeholderText: 'Loading preview...', loading: true });

  try {
    const gender = resolveGenderForTool(toolKey);
    const payload = await fetchCache.fetchCharacterSprite(
      charId,
      FEMALE_DEFAULT_STANDING,
      previewOutfitId,
      gender,
      config.fullSize !== false
    );
    const spriteUrl = payload?.spriteUrl || payload?.sprite_url || null;

    if (!spriteUrl) {
      setPreviewCardState(cardEl, { placeholderText: 'No preview available for this outfit yet' });
      return;
    }
    setPreviewCardState(cardEl, { imgUrl: spriteUrl, showImg: true });
  } catch (error) {
    console.warn(`[CreateVideo] Tool ${String(toolKey)} preview failed:`, error);
    setPreviewCardState(cardEl, { placeholderText: 'Failed to load preview' });
  }
};

const attachPreviewControls = (toolKey) => {
  const config = TOOL_PREVIEW_CONFIG[toolKey];
  if (!config) return;
  const els = getElements();
  const cardEl = els[config.cardKey];
  const outfitSelectEl = els[config.outfitKey];
  const outfitSelectId = config.outfitSelectId;

  if (cardEl) {
    addSwipeSupport(cardEl, (direction) => {
      if (isSearchDropdownOpen(outfitSelectId)) return;
      cycleSelect(outfitSelectEl, direction === 'left' ? 1 : -1);
    });
  }

  const bindArrow = (buttonEl, direction) => {
    if (!buttonEl || !outfitSelectEl) return;
    buttonEl.addEventListener('click', () => cycleSelect(outfitSelectEl, direction));
    buttonEl.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        cycleSelect(outfitSelectEl, direction);
      }
    });
  };

  bindArrow(els[config.leftArrowKey], -1);
  bindArrow(els[config.rightArrowKey], 1);
};

const updateToolArrowVisibility = (toolKey) => {
  const config = TOOL_PREVIEW_CONFIG[toolKey];
  if (!config) return;
  const els = getElements();
  const showOutfit = getSelectableOptions(els[config.outfitKey]).length > 1;
  els[config.leftArrowKey]?.classList.toggle('hidden', !showOutfit);
  els[config.rightArrowKey]?.classList.toggle('hidden', !showOutfit);
};

const attachReceiverSwipeControls = () => {
  ['receiver-outfit-3', 'receiver-outfit-4', 'receiver-outfit-free-video'].forEach((outfitId) => {
    const outfitSelectEl = document.getElementById(outfitId);
    const outfitHost = document.querySelector(`[data-search-root="${outfitId}"]`);
    if (!outfitHost) return;
    addSwipeSupport(/** @type {HTMLElement} */ (outfitHost), (direction) => {
      if (isSearchDropdownOpen(outfitId)) return;
      cycleSelect(outfitSelectEl, direction === 'left' ? 1 : -1);
    });
  });
};

const setActiveMode = (mode) => {
  state.mode = mode;
  const els = getElements();
  els.modeTabs.forEach((tab) => {
    const isActive = tab.id === `mode-${mode}`;
    tab.classList.toggle('create-mode-tab--active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
  });

  els.panels.forEach((panel) => {
    const isActive = panel.id === `panel-${mode}`;
    panel.classList.toggle('hidden', !isActive);
    /** @type {HTMLElement} */ (panel).hidden = !isActive;
  });
};

const setSpriteFlow = (flow) => {
  const nextFlow = flow === SPRITE_FLOWS.CUSTOM ? SPRITE_FLOWS.CUSTOM : SPRITE_FLOWS.OUTFIT;
  state.tool3.spriteFlow = nextFlow;
  const els = getElements();
  els.spriteFlowTabs.forEach((tab) => {
    const isActive = tab.id === `sprite-flow-${nextFlow}`;
    tab.classList.toggle('create-mode-tab--active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
  });

  const showOutfit = nextFlow === SPRITE_FLOWS.OUTFIT;
  if (els.spriteSectionRef) {
    els.spriteSectionRef.classList.toggle('hidden', !showOutfit);
    els.spriteSectionRef.hidden = !showOutfit;
  }
  if (els.customImageFlow3) {
    els.customImageFlow3.classList.toggle('hidden', showOutfit);
    els.customImageFlow3.hidden = showOutfit;
    els.customImageFlow3.setAttribute('aria-hidden', showOutfit ? 'true' : 'false');
  }
};

const handleSpriteFlowTabClick = (event) => {
  const tab = event.target.closest('.create-mode-tab');
  if (!tab?.id?.startsWith('sprite-flow-')) return;
  setSpriteFlow(tab.id.replace('sprite-flow-', ''));
};

const handleSpriteFlowTabKeydown = (event) => {
  const tabs = Array.from(getElements().spriteFlowTabs);
  const currentIndex = tabs.indexOf(event.target);

  let nextIndex = currentIndex;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    nextIndex = (currentIndex + 1) % tabs.length;
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = tabs.length - 1;
  } else {
    return;
  }

  event.preventDefault();
  /** @type {HTMLElement} */ (tabs[nextIndex]).focus();
  /** @type {HTMLElement} */ (tabs[nextIndex]).click();
};

const handleModeTabClick = (event) => {
  const tab = event.target.closest('.create-mode-tab');
  if (!tab) return;
  const mode = tab.id.replace('mode-', '');
  setActiveMode(mode);
  if (mode === MODES.VIDEO_TEXT) hydrateVideoTool({ defaultChar: true });
  else hydrateImageTool();
};

const handleModeTabKeydown = (event) => {
  const tabs = Array.from(getElements().modeTabs);
  const currentIndex = tabs.indexOf(event.target);

  let nextIndex = currentIndex;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    nextIndex = (currentIndex + 1) % tabs.length;
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = tabs.length - 1;
  } else {
    return;
  }

  event.preventDefault();
  /** @type {HTMLElement} */ (tabs[nextIndex]).focus();
  /** @type {HTMLElement} */ (tabs[nextIndex]).click();
};

const loadCharacters = async () => {
  try {
    const [rankings, modalData] = await Promise.all([
      fetchCache.fetchCharacterRankings(),
      fetchCache.fetchJSON(catalogJsonUrl('data/characters/characters_rankings_modal.json')),
    ]);

    let privateList = [];
    const authToken = await getAuthToken();
    if (authToken) {
      try {
        privateList = await fetchPrivateCharacters();
      } catch (e) {
        console.warn('[CreateVideo] Failed to fetch private characters:', e);
      }
    }

    state.characters = mapCreateCharacters({ rankings, modalData, privateList });
    return state.characters;
  } catch (error) {
    console.error('[CreateVideo] Failed to load characters:', error);
    return [];
  }
};

const loadOutfits = async () => {
  try {
    const lang = storage.get('displayLanguage') || 'en';
    const data = await fetchCache.fetchOutfits(lang);
    state.outfits = unwrapOutfitsPayload(data);
    return state.outfits;
  } catch (error) {
    console.error('[CreateVideo] Failed to load outfits:', error);
    return [];
  }
};

const loadMappings = async () => {
  try {
    state.actionMappings = await fetchCache.fetchJSON(`${WORKER_URL}/mappings/actions`);
  } catch (error) {
    console.warn('[CreateVideo] Failed to load action mappings, using fallback:', error);
    state.actionMappings = {
      0: { name: 'base_sprites', hasLora: false },
    };
  }
};

const populateCharacterDropdown = (selectId) => {
  const selectEl = document.getElementById(selectId);
  if (!selectEl) return;

  const defaultLabel = getCreateVideoT('select_character', 'Select a character');
  selectEl.innerHTML = `<option value="">${defaultLabel}</option>`;
  sortCharactersByRankAsc(state.characters).forEach((char) => {
    const option = document.createElement('option');
    option.value = char.id;
    option.textContent = char.name;
    selectEl.appendChild(option);
  });
};

const populateOutfitDropdown = async (selectId, characterId) => {
  const selectEl = document.getElementById(selectId);
  if (!selectEl) return;

  const previousValue = selectEl.value;
  const defaultLabel = getCreateVideoT('select_outfit', 'Select an outfit');
  selectEl.innerHTML = `<option value="">${defaultLabel}</option>`;

  if (!characterId) return;

  const outfitFallbackT = getCreateVideoT('outfit_fallback', 'Outfit {id}');
  const outfitNotAvailableT = getCreateVideoT('outfit_not_available', 'This outfit is not available for this character');

  const characterOutfitIds = new Set(await fetchCharacterOutfitIds(characterId));

  let outfits = state.outfits || [];
  if (selectId === 'receiver-outfit-3' || selectId === 'receiver-outfit-free-video') {
    const gender = resolveGenderForCharacterId(characterId);
    outfits = sortOutfitsByIdAsc(filterOutfitsByGender(outfits, gender));
  }

  outfits.forEach((outfit) => {
    const outfitId = String(outfit.id);
    const option = document.createElement('option');
    option.value = outfitId;
    option.textContent = outfit.name || interpolate(outfitFallbackT, { id: outfitId });
    if (!characterOutfitIds.has(outfitId)) {
      option.classList.add('option--unavailable');
      option.setAttribute('data-unavailable', 'true');
      option.title = outfitNotAvailableT;
    }
    selectEl.appendChild(option);
  });

  if (selectId === 'receiver-outfit-3' || selectId === 'receiver-outfit-free-video') {
    const options = getSelectableOptions(selectEl);
    if (previousValue && options.some((opt) => opt.value === previousValue)) {
      selectEl.value = previousValue;
    } else {
      selectEl.value = '';
    }
  }
};

const initializeSearchDropdowns = (mode = state.mode) => {
  const dropdownIds = mode === MODES.VIDEO_TEXT
    ? ['receiver-char-4', 'receiver-outfit-4', 'receiver-char-free-video', 'receiver-outfit-free-video']
    : ['receiver-char-3', 'receiver-outfit-3'];

  dropdownIds.forEach((id) => {
    if (!document.getElementById(id)) return;
    const placeholder = id.includes('char')
      ? getCreateVideoT('select_character', 'Select a character')
      : getCreateVideoT('select_outfit', 'Select an outfit');
    initSearchDropdown(id, { placeholder, searchLabel: placeholder, searchPlaceholder: placeholder });

    // Replace Create Video character dropdown interaction with Rankings-style card modal.
    if (id.includes('char')) {
      attachCreateVideoRankedCharacterPickerToTrigger({
        selectId: id,
        getCharacters: () => state.characters,
      });
    }
  });
};

const fetchSprites = async (characterId, outfitId) => {
  if (!characterId || !hasOutfitId(outfitId)) return [];
  try {
    const url = withSfwQueryString(`${WORKER_URL}/get_character_sprites?character_id=${characterId}&outfit_id=${outfitId}`);
    const data = await fetchCache.fetchJSON(url);
    return (data.sprites || []).map((sprite) => ({
      ...sprite,
      char_id: sprite.char_id ?? characterId,
      outfit_id: sprite.outfit_id ?? outfitId,
      actionName: resolveMappedActionName(sprite.action_id, sprite),
    }));
  } catch (error) {
    console.error('[CreateVideo] Failed to fetch sprites:', error);
    return [];
  }
};

const attachComparisonSpriteContextMenu = (img, sprite, fallbackIds = null) => {
  attachSpriteContextMenuListeners(img, {
    getOverrideSpriteData: (mediaEl) => {
      const characterId = sprite.char_id ?? fallbackIds?.charId;
      const outfitId = sprite.outfit_id ?? fallbackIds?.outfitId;
      const actionId = sprite.action_id;
      if (characterId == null || characterId === '' || actionId == null || actionId === '') return false;
      return {
        characterId,
        outfitId,
        actionId,
        actionName: sprite.actionName || '',
        spriteUrl: mediaEl.currentSrc || mediaEl.src || '',
        path: null,
        isVideo: false,
        source: 'sprite-comparison-image'
      };
    }
  });
};

const resolvePreviewCardOverride = (cardEl, mediaEl) => {
  const mediaUrl = mediaEl.currentSrc || mediaEl.src || '';
  const parsed = parseSpriteUrl(mediaUrl);
  const isVideo = mediaEl.tagName === 'VIDEO';
  const els = getElements();

  if (cardEl === els.spritePreviewCard3) {
    const generated = state.tool3.preview.generatedFor;
    const characterId = generated?.charId || parsed.characterId;
    const outfitId = generated?.outfitId ?? parsed.outfitId;
    const actionId = parsed.actionId;
    if (characterId == null || characterId === '' || actionId == null || actionId === '') return false;
    return {
      characterId,
      outfitId,
      actionId,
      spriteUrl: mediaUrl,
      path: null,
      isVideo: false,
      source: 'character-preview-card'
    };
  }

  if (cardEl === els.targetPreviewCard4) {
    const selected = state.tool4.selectedSpriteKey
      ? (state.tool4.sprites || []).find((sprite) => spriteKey(sprite) === state.tool4.selectedSpriteKey)
      : null;
    const characterId = els.receiverChar4?.value || parsed.characterId;
    const outfitId = els.receiverOutfit4?.value !== '' && els.receiverOutfit4?.value !== undefined
      ? els.receiverOutfit4.value
      : (selected?.outfit_id ?? parsed.outfitId);
    const actionId = selected?.action_id ?? parsed.actionId ?? FEMALE_DEFAULT_STANDING;
    if (!characterId || actionId == null || actionId === '') return false;
    return {
      characterId,
      outfitId,
      actionId,
      actionName: selected?.actionName || '',
      spriteUrl: mediaUrl,
      videoActionId: parsed.videoActionId,
      path: null,
      isVideo,
      source: 'character-preview-card'
    };
  }

  const characterId = els.receiverChar3?.value || parsed.characterId;
  const outfitId = els.receiverOutfit3?.value !== '' && els.receiverOutfit3?.value !== undefined
    ? els.receiverOutfit3.value
    : parsed.outfitId;
  const actionId = parsed.actionId ?? FEMALE_DEFAULT_STANDING;
  if (!characterId || actionId == null || actionId === '') return false;
  return {
    characterId,
    outfitId,
    actionId,
    spriteUrl: mediaUrl,
    path: null,
    isVideo: false,
    source: 'character-preview-card'
  };
};

const bindPreviewCardContextMenus = () => {
  const els = getElements();
  [els.targetPreviewCard3, els.spritePreviewCard3, els.targetPreviewCard4].forEach((cardEl) => {
    if (!cardEl) return;
    attachSpriteContextMenuListeners(cardEl, {
      getMediaEl: () => cardEl.querySelector('.character-preview-card__media:not(.hidden)'),
      getOverrideSpriteData: (mediaEl) => resolvePreviewCardOverride(cardEl, mediaEl)
    });
  });
};

const getCreatableSelectedActionIds = () => {
  const existingKeys = new Set((state.tool1.receiverSprites || []).map(spriteKey));
  return Array.from(state.tool1.selectedKeys)
    .filter((key) => !existingKeys.has(key))
    .map((key) => parseInt(key, 10))
    .filter((actionId) => Number.isFinite(actionId));
};

const pruneSelectedKeysAgainstExisting = () => {
  const existingKeys = new Set((state.tool1.receiverSprites || []).map(spriteKey));
  for (const key of Array.from(state.tool1.selectedKeys)) {
    if (existingKeys.has(key)) state.tool1.selectedKeys.delete(key);
  }
};

const renderComparisonGrid = (gridEl, referenceItems, receiverItems, selectedKeys, forcedKeys, onToggle) => {
  if (!gridEl) return;
  gridEl.innerHTML = '';

  if (!referenceItems.length) {
    const emptyLabel = gridEl.id === 'sprite-grid-ref'
      ? getCreateVideoT('no_images_to_display', 'No images to display')
      : 'No images to display';
    gridEl.innerHTML = `<div class="sprite-grid-empty">${emptyLabel}</div>`;
    return;
  }

  const receiverKeySet = new Set(receiverItems.map(spriteKey));

  referenceItems.forEach((item) => {
    const key = spriteKey(item);
    const exists = receiverKeySet.has(key);
    const isSelected = selectedKeys.has(key);
    const isForced = forcedKeys?.has(key);
    const displayName = formatSpriteNameForDisplay(item.actionName) || 'action';

    const itemEl = document.createElement('div');
    itemEl.className = 'sprite-comparison-item';
    if (exists) itemEl.classList.add('sprite-item--exists');
    else if (isSelected) itemEl.classList.add('sprite-item--selected');
    else itemEl.classList.add('sprite-item--missing');

    if (isForced) itemEl.classList.add('sprite-item--forced');

    itemEl.setAttribute('role', 'checkbox');
    itemEl.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    itemEl.setAttribute('aria-label', displayName);
    itemEl.setAttribute('tabindex', exists ? '-1' : '0');
    itemEl.dataset.key = key;

    const img = document.createElement('img');
    const imagePath = getSpriteSmallImagePath(item);
    img.src = imagePath.startsWith('http') ? imagePath : `${CDN_URL}${imagePath}`;
    img.alt = displayName;
    img.loading = 'lazy';
    img.className = 'sprite-comparison-image';
    attachComparisonSpriteContextMenu(img, item, getSpriteRefReceiverIds());

    const label = document.createElement('div');
    label.className = 'sprite-comparison-label';
    const labelAction = document.createElement('span');
    labelAction.className = 'sprite-label-action';
    labelAction.textContent = displayName;
    label.appendChild(labelAction);

    itemEl.appendChild(img);
    itemEl.appendChild(label);

    if (!exists) {
      const handleToggle = () => {
        if (isForced) return;
        onToggle(key, item);
      };
      itemEl.addEventListener('click', handleToggle);
      itemEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      });
    }

    gridEl.appendChild(itemEl);
  });
  applySpriteNameFilters();
};

const setCreditLineText = (el, text) => {
  if (!el) return;
  const lineEl = el.querySelector('[data-credit-line]') || el.querySelector('span:last-of-type');
  if (lineEl) lineEl.textContent = text;
  else el.textContent = text;
};

const updateCreditDisplayWithTotal = (creditInfoEl, totalCost, availableCredits = null) => {
  if (!creditInfoEl) return;
  const available = availableCredits ?? getUserCredits();
  const hasEnough = totalCost <= available;

  creditInfoEl.classList.remove('hidden');
  const template = creditInfoEl.id === 'credit-info-ref'
    ? getCreateVideoT('credits_usage_ref', 'This will use {totalCost} / {availableCredits} credits')
    : 'This will use {totalCost} / {availableCredits} credits';
  setCreditLineText(creditInfoEl, interpolate(template, { totalCost: String(totalCost), availableCredits: String(available) }));
  creditInfoEl.classList.toggle('insufficient', !hasEnough);
};

const renderCreditProTip = (el, templateKey, fallbackTemplate, { count, cost, available }, options = {}) => {
  if (!el) return;
  const template = getCreateVideoT(templateKey, fallbackTemplate);
  const line = interpolateCreditTemplate(template, {
    count: String(count),
    cost: String(cost),
    available: String(available ?? getUserCredits()),
  });
  const showLabel = options.showLabel !== false;
  const labelEl = el.querySelector('.pro-tip-text__label');
  if (labelEl) labelEl.classList.toggle('hidden', !showLabel);
  if (showLabel && labelEl) labelEl.textContent = getCreateVideoT('pro_tip', 'Pro tip:');
  setCreditLineText(el, line);
  el.classList.toggle('insufficient', cost > (available ?? getUserCredits()));
};

const updateTool1Grid = async () => {
  const els = getElements();
  if (!els.spriteSectionRef) return;

  const { charId: receiverCharId, outfitId: receiverOutfitId } = getSpriteRefReceiverIds();

  if (!receiverCharId || !hasOutfitId(receiverOutfitId)) {
    if (els.spriteGridRef) els.spriteGridRef.innerHTML = '';
    if (els.spriteStatusRef) els.spriteStatusRef.textContent = getCreateVideoT('select_characters_to_view', 'Select a character and outfit to view available images');
    if (els.creditInfoRef) els.creditInfoRef.classList.add('hidden');
    if (els.validateSpriteRef) els.validateSpriteRef.disabled = true;
    if (els.customPromptAdvancedRef) els.customPromptAdvancedRef.classList.add('hidden');
    if (els.customOutfitPromptRef) els.customOutfitPromptRef.value = '';
    if (els.customOutfitNegativeRef) els.customOutfitNegativeRef.value = '';
    setCustomImageFlowEnabled(false);
    return;
  }

  els.spriteStatusRef.textContent = getCreateVideoT('loading_images', 'Loading images...');

  const gridSprites = await fetchGridSprites(receiverCharId, receiverOutfitId);
  const receiverSprites = gridSprites.filter(
    (s) => Number(s.char_id) === Number(receiverCharId) && Number(s.outfit_id) === Number(receiverOutfitId)
  );

  state.tool1.receiverSprites = receiverSprites;

  const gridActionIds = new Set(gridSprites.map((s) => s.action_id));
  const hasNoImages = receiverSprites.length === 0;
  if (hasNoImages) {
    state.tool1.forcedKeys = new Set(
      FEMALE_STANDING_ACTION_IDS.filter((aid) => gridActionIds.has(aid)).map(String)
    );
    state.tool1.selectedKeys = new Set(state.tool1.forcedKeys);
  } else {
    state.tool1.forcedKeys = new Set();
    pruneSelectedKeysAgainstExisting();
  }

  const outfit = state.outfits?.find((o) => String(o.id) === String(receiverOutfitId));
  const outfitName = outfit?.name || '';
  const titleEl = document.getElementById('sprite-section-title');

  if (hasNoImages) {
    const character = state.characters?.find((c) => String(c.id) === String(receiverCharId));
    const characterName = character?.name || '';
    const titleTemplate = getCreateVideoT('no_images_for_outfit', "{character_name} doesn't yet have {outfit_name}. Create the images?");
    if (titleEl) titleEl.textContent = interpolate(String(titleTemplate), { character_name: characterName, outfit_name: outfitName });

    if (els.customPromptAdvancedRef) els.customPromptAdvancedRef.classList.remove('hidden');
    if (els.customPromptLabelRef) {
      const labelTemplate = getCreateVideoT('custom_outfit_prompt_label', 'Optional: replace the outfit definition with your own prompt. It will still be called {outfit_name}');
      els.customPromptLabelRef.textContent = interpolate(String(labelTemplate), { outfit_name: outfitName });
    }
    const isFree = (storage.get('userData')?.current_tier || 'free') === 'free';
    if (els.customOutfitPromptRef) {
      els.customOutfitPromptRef.disabled = isFree;
      syncRichPromptEditorDisabledState(els.customOutfitPromptRef);
    }
    if (els.customOutfitNegativeRef) {
      els.customOutfitNegativeRef.disabled = isFree;
      syncRichPromptEditorDisabledState(els.customOutfitNegativeRef);
    }
    if (els.customPromptTierNoticeRef) els.customPromptTierNoticeRef.classList.toggle('hidden', !isFree);
    ensureOutfitPromptAutocomplete();
  } else {
    if (titleEl) {
      const titleTemplate = getCreateVideoT('select_images_for_outfit', '2. Select images to create for {outfit_name}');
      titleEl.textContent = interpolate(String(titleTemplate), { outfit_name: outfitName });
    }
    if (els.customPromptAdvancedRef) {
      els.customPromptAdvancedRef.classList.add('hidden');
      els.customPromptAdvancedRef.removeAttribute('open');
    }
    if (els.customOutfitPromptRef) els.customOutfitPromptRef.value = '';
    if (els.customOutfitNegativeRef) els.customOutfitNegativeRef.value = '';
  }

  setCustomImageFlowEnabled(!hasNoImages);

  const handleToggle = (key) => {
    if (state.tool1.forcedKeys.has(key)) return;
    if (state.tool1.selectedKeys.has(key)) {
      state.tool1.selectedKeys.delete(key);
    } else {
      state.tool1.selectedKeys.add(key);
    }
    const itemEl = els.spriteGridRef?.querySelector(`[data-key="${CSS.escape(String(key))}"]`);
    if (itemEl && !itemEl.classList.contains('sprite-item--exists')) {
      const isSelected = state.tool1.selectedKeys.has(key);
      itemEl.classList.toggle('sprite-item--selected', isSelected);
      itemEl.classList.toggle('sprite-item--missing', !isSelected);
      itemEl.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    }
    updateTool1Credits();
  };

  const renderTool1Grid = () => {
    renderComparisonGrid(
      els.spriteGridRef,
      gridSprites,
      receiverSprites,
      state.tool1.selectedKeys,
      state.tool1.forcedKeys,
      handleToggle
    );
  };

  renderTool1Grid();
  updateTool1Credits();

  const missingCount = gridSprites.length - receiverSprites.length;
  const statusTemplate = getCreateVideoT('images_count_available', '{count} images, {available} available to create');
  els.spriteStatusRef.textContent = interpolate(statusTemplate, { count: String(gridSprites.length), available: String(missingCount) });
};

const updateTool1Credits = () => {
  const els = getElements();
  if (!els.creditInfoRef) return;

  const actionIds = getCreatableSelectedActionIds();
  if (actionIds.length === 0) {
    updateCreditDisplayWithTotal(els.creditInfoRef, 0);
    if (els.validateSpriteRef) els.validateSpriteRef.disabled = true;
    return;
  }

  const { charId, outfitId } = getSpriteRefReceiverIds();
  if (!charId || !hasOutfitId(outfitId)) {
    els.creditInfoRef.classList.add('hidden');
    if (els.validateSpriteRef) els.validateSpriteRef.disabled = true;
    return;
  }

  const total = computeImageBatchTotal({
    characterId: charId,
    actionIds,
    characters: state.characters,
    actionMappings: state.actionMappings,
  });
  const result = buildLocalCreditResult(total);

  updateCreditDisplayWithTotal(els.creditInfoRef, result.total, result.available_credits);
  if (els.validateSpriteRef) {
    els.validateSpriteRef.disabled = !result.has_enough_credits;
  }
};

const updateTool3Credits = () => {
  const els = getElements();
  if (!els.creditInfoSpriteText) return;

  const charId = els.receiverChar3?.value || null;
  const total = computeImageUnitCost({
    characterHasLora: getCharacterHasLora(charId, state.characters),
    actionHasLora: Boolean(buildTool3LoraPayload()),
    style: getCharacterStyle(charId, state.characters),
  });
  const result = buildLocalCreditResult(total);

  renderCreditProTip(
    els.creditInfoSpriteText,
    'credit_per_image',
    '1 image = {cost} credits (You have {available} credits)',
    { count: 1, cost: result.total, available: result.available_credits },
  );
  validateTool3Form();
};

const buildTool3LoraPayload = () => {
  if (!getNsfwEnabled()) return null;
  const els = getElements();
  const loraId = els.spriteLoraId3?.value?.trim() || '';
  if (!loraId) return null;
  const strengthRaw = els.spriteLoraStrength3?.value?.trim() || '';
  const strengthParsed = strengthRaw ? parseFloat(strengthRaw) : NaN;
  return {
    id: loraId,
    source: 'civitai',
    strength: Number.isFinite(strengthParsed) ? strengthParsed : 1.0,
  };
};

const getSelectedVideoLoraCount = () => {
  const actionId = state.tool4.selectedSpriteKey;
  if (actionId == null) return 0;
  const video = state.tool4.videos.find((entry) => String(entry.action_id) === String(actionId));
  if (!video) return 0;
  if (video.video_lora_count != null && Number.isFinite(Number(video.video_lora_count))) {
    return Math.max(0, Number(video.video_lora_count));
  }
  return video.has_video_lora ? 1 : 0;
};

const validateTool3Form = () => {
  const els = getElements();
  if (!els.validateSpriteText) return;
  if (els.customImageFlow3 && !state.tool3.customFlowEnabled) {
    els.validateSpriteText.disabled = true;
    return;
  }
  const charId = els.receiverChar3?.value || '';
  const outfitId = els.receiverOutfit3?.value || '';
  const prompt = els.promptText3?.value?.trim() || '';
  const spriteName = els.spriteName3?.value?.trim() || '';
  const cost = computeImageUnitCost({
    characterHasLora: getCharacterHasLora(charId, state.characters),
    actionHasLora: Boolean(buildTool3LoraPayload()),
    style: getCharacterStyle(charId, state.characters),
  });
  const hasEnough = cost <= getUserCredits();
  els.validateSpriteText.disabled = !(charId && outfitId !== '' && prompt && spriteName && hasEnough);
};

const updateTool4SpriteGrid = async () => {
  const els = getElements();
  const charId = els.receiverChar4?.value;
  const outfitId = els.receiverOutfit4?.value;

  if (!charId || !hasOutfitId(outfitId)) {
    els.spriteSelectionSection4?.classList.add('hidden');
    await updateToolPreview(4);
    return;
  }

  els.spriteSelectionSection4?.classList.remove('hidden');

  els.spriteStatusVideo.textContent = 'Loading images and videos...';
  setPreviewCardState(els.targetPreviewCard4, { imgUrl: '', showImg: false, placeholderText: 'Loading preview...' });

  const [sprites, videos] = await Promise.all([
    fetchSprites(charId, outfitId),
    fetchCharacterVideos(charId, outfitId),
  ]);

  state.tool4.sprites = sprites;
  state.tool4.videos = videos;
  state.tool4.selectedSpriteKey = null;

  // Per-action video status: 1=HD done, 2=failed, 3=upgrading, 4=low-res done
  // (upgradable), 5/13=generating. Missing or failed means a new video can be created.
  const videoByKey = new Map(videos.map((v) => [spriteKey(v), v]));

  els.spriteGridForVideo.innerHTML = '';
  sprites.forEach((sprite) => {
    const key = spriteKey(sprite);
    const video = videoByKey.get(key);
    const status = video?.status;
    const canCreate = !video || status === 2;
    const canUpgrade = status === 4;
    const selectable = canCreate || canUpgrade;
    const displayName = formatSpriteNameForDisplay(sprite.actionName) || 'action';

    const itemEl = document.createElement('div');
    itemEl.className = 'sprite-comparison-item';
    itemEl.classList.add(canCreate ? 'sprite-item--missing' : 'sprite-item--exists');

    itemEl.setAttribute('role', 'radio');
    itemEl.setAttribute('aria-checked', 'false');
    itemEl.setAttribute('aria-label', displayName);
    itemEl.setAttribute('tabindex', selectable ? '0' : '-1');
    itemEl.dataset.key = key;

    const img = document.createElement('img');
    const imagePath = getSpriteSmallImagePath(sprite);
    img.src = `${CDN_URL}${imagePath}`;
    img.alt = displayName;
    img.loading = 'lazy';
    img.className = 'sprite-comparison-image';
    attachComparisonSpriteContextMenu(img, sprite, {
      charId: els.receiverChar4?.value,
      outfitId: els.receiverOutfit4?.value
    });

    const badgeText = getVideoBadgeText(status);
    if (badgeText) {
      const badge = document.createElement('span');
      badge.className = 'sprite-has-video-badge';
      badge.textContent = badgeText;
      itemEl.appendChild(badge);
    }

    const label = document.createElement('div');
    label.className = 'sprite-comparison-label';
    const labelAction = document.createElement('span');
    labelAction.className = 'sprite-label-action';
    labelAction.textContent = displayName;
    label.appendChild(labelAction);

    itemEl.appendChild(img);
    itemEl.appendChild(label);

    if (selectable) {
      const selectMode = canUpgrade ? 'upgrade' : 'create';
      itemEl.addEventListener('click', () => selectTool4Sprite(key, sprite, selectMode));
      itemEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectTool4Sprite(key, sprite, selectMode);
        }
      });
    }

    els.spriteGridForVideo.appendChild(itemEl);
  });
  applySpriteNameFilters();

  const creatableCount = sprites.filter((s) => {
    const v = videoByKey.get(spriteKey(s));
    return !v || v.status === 2;
  }).length;
  const upgradableCount = videos.filter((v) => v.status === 4).length;
  els.spriteStatusVideo.textContent =
    `${sprites.length} images, ${creatableCount} available for new videos, ${upgradableCount} upgradable to HD`;
  await updateToolPreview(4);

  // Auto-select an action when arriving from a hangout or notification deep link.
  if (state.tool4.pendingSelectActionId) {
    const pendingCharId = state.tool4.pendingSelectCharId;
    const pendingOutfitId = state.tool4.pendingSelectOutfitId;
    const isMatchingPrefill = !pendingCharId || !pendingOutfitId
      || (String(charId) === String(pendingCharId) && String(outfitId) === String(pendingOutfitId));
    if (isMatchingPrefill) {
      const targetKey = state.tool4.pendingSelectActionId;
      const intent = state.tool4.pendingSelectIntent;
      state.tool4.pendingSelectActionId = null;
      state.tool4.pendingSelectIntent = null;
      state.tool4.pendingSelectCharId = null;
      state.tool4.pendingSelectOutfitId = null;
      const sprite = sprites.find((s) => spriteKey(s) === targetKey);
      const video = videoByKey.get(targetKey);
      const canCreate = !video || video.status === 2;
      const canUpgrade = video?.status === 4;
      if (sprite && intent === 'upgrade' && canUpgrade) {
        selectTool4Sprite(targetKey, sprite, 'upgrade');
      } else if (sprite && intent !== 'upgrade' && canCreate) {
        selectTool4Sprite(targetKey, sprite, 'create');
      } else if (sprite && intent !== 'upgrade' && canUpgrade) {
        selectTool4Sprite(targetKey, sprite, 'upgrade');
      }
    }
  }
};

const selectTool4Sprite = (key, sprite, selectMode = 'create') => {
  const els = getElements();
  state.tool4.selectedSpriteKey = key;
  state.tool4.selectMode = selectMode;

  els.spriteGridForVideo.querySelectorAll('.sprite-comparison-item').forEach((item) => {
    const isSelected = item.dataset.key === key;
    item.classList.toggle('sprite-item--selected', isSelected);
    item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
  });

  updateTool4SelectionPreview(key, sprite, selectMode);

  if (selectMode === 'upgrade') {
    showTool4UpgradeForm();
  } else {
    showTool4CreateForm();
  }
};

// Reflect the grid selection in the target preview card: the source image when
// creating a video, or the existing low-res video when upgrading.
const updateTool4SelectionPreview = (key, sprite, selectMode) => {
  const els = getElements();
  if (!els.targetPreviewCard4) return;

  if (selectMode === 'upgrade') {
    const video = state.tool4.videos.find((v) => spriteKey(v) === key);
    const charId = els.receiverChar4?.value;
    const outfitId = els.receiverOutfit4?.value;
    if (video?.action_id != null && charId && outfitId !== '' && outfitId !== undefined) {
      const videoUrl = FetchCache.buildVideoUrl(charId, outfitId, video.action_id);
      setPreviewCardState(els.targetPreviewCard4, { videoUrl, showVideo: true });
      return;
    }
  }

  const imagePath = sprite ? getSpriteSmallImagePath(sprite) : '';
  const imgUrl = imagePath ? `${CDN_URL}${imagePath}` : '';
  setPreviewCardState(els.targetPreviewCard4, { imgUrl, showImg: !!imagePath });
};

const showTool4CreateForm = () => {
  const els = getElements();
  els.upgradeVideoForm4?.classList.add('hidden');
  els.createVideoForm4.classList.remove('hidden');
  updateTool4Credits();
  validateTool4Form();
};

const showTool4UpgradeForm = () => {
  const els = getElements();
  els.createVideoForm4.classList.add('hidden');
  if (!els.upgradeVideoForm4) return;
  els.upgradeVideoForm4.classList.remove('hidden');

  updateTool4UpgradeCredits();
};

const hideTool4CreateForm = () => {
  const els = getElements();
  els.createVideoForm4.classList.add('hidden');
  els.upgradeVideoForm4?.classList.add('hidden');
  state.tool4.selectedSpriteKey = null;
  state.tool4.selectMode = 'create';
  els.cameraText4.value = '';
  spriteVideoLoras?.reset();
  if (els.videoAdvancedLora) els.videoAdvancedLora.removeAttribute('open');

  els.spriteGridForVideo.querySelectorAll('.sprite-comparison-item').forEach((item) => {
    item.classList.remove('sprite-item--selected');
    item.setAttribute('aria-checked', 'false');
  });
};

const updateTool4Credits = () => {
  const els = getElements();
  if (!els.creditInfoVideoText) return;

  const userVideoLora = buildTool4LoraPayload({ strict: false });
  const overLora = Boolean(spriteVideoLoras?.overLimit());
  const total = computeVideoUnitCost({
    operation: 'video_create',
    userVideoLora,
    maxLoras: SPRITE_VIDEO_LORA_MAX,
  });
  if (els.videoLoraLimit) {
    els.videoLoraLimit.hidden = !overLora;
    els.videoLoraLimit.classList.toggle('hidden', !overLora);
    if (overLora) els.videoLoraLimit.textContent = spriteVideoLoras.limitMessage();
  }
  const result = buildLocalCreditResult(total);

  renderCreditProTip(
    els.creditInfoVideoText,
    'credits_per_video',
    '1 video = {cost} credits (You have {available} credits)',
    { count: 1, cost: result.total, available: result.available_credits },
    { showLabel: false },
  );
  validateTool4Form();
};

const updateTool4UpgradeCredits = () => {
  const els = getElements();
  if (!els.creditInfoUpgradeText) return;

  const total = computeVideoUnitCost({
    operation: 'video_hd',
    loraCount: getSelectedVideoLoraCount(),
  });
  const result = buildLocalCreditResult(total);

  renderCreditProTip(
    els.creditInfoUpgradeText,
    'credits_per_hd_upgrade',
    'HD upgrade = {cost} credits (You have {available} credits)',
    { count: 1, cost: result.total, available: result.available_credits },
    { showLabel: false },
  );

  if (els.validateVideoUpgrade) {
    els.validateVideoUpgrade.disabled = !result.has_enough_credits;
  }
};

const validateTool4Form = () => {
  const els = getElements();
  const hasSprite = !!state.tool4.selectedSpriteKey;
  const prompt = els.cameraText4?.value?.trim() || '';
  const userVideoLora = buildTool4LoraPayload({ strict: false });
  const cost = computeVideoUnitCost({
    operation: 'video_create',
    userVideoLora,
    maxLoras: SPRITE_VIDEO_LORA_MAX,
  });
  const hasEnough = cost <= getUserCredits();
  const overLora = Boolean(spriteVideoLoras?.overLimit());
  els.validateVideoText.disabled = !(hasSprite && prompt && hasEnough && !overLora);
};

const buildTool4LoraPayload = ({ strict = true } = {}) => {
  if (!getNsfwEnabled()) return null;
  return spriteVideoLoras?.collect({ strict }) ?? null;
};

const setButtonBusy = (btn, isBusy) => {
  if (!btn) return;
  btn.classList.toggle('is-busy', isBusy);
  btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
};

const notifyJobStatus = (kind, status, data) => {
  const noun = kind === 'video' ? 'Video' : 'Image';
  if (status === 'completed') {
    noteSseToast();
    showNotification(`${noun} completed successfully!`, 'success');
  } else if (status === 'failed') {
    showNotification(`${noun} generation failed.`, 'error');
  } else if (status === 'error') {
    showNotification(data?.error || `Error checking ${noun.toLowerCase()} status`, 'error');
  } else if (status === 'timeout' || status === 'wait-timeout') {
    showNotification(`${noun} generation is taking longer than expected. Please check back later.`, 'info');
  }
};

const setTool3PreviewVisibility = (shouldShow) => {
  const els = getElements();
  if (!els.spritePreviewSection3) return;
  els.spritePreviewSection3.classList.toggle('hidden', !shouldShow);
  els.spritePreviewSection3.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
};

const renderTool3PreviewCard = ({ imageUrl = '', loading = false } = {}) => {
  const els = getElements();
  if (!els.spritePreviewCard3) return;

  if (loading) {
    setPreviewCardState(els.spritePreviewCard3, { loading: true, placeholderText: '' });
  } else if (!imageUrl) {
    setPreviewCardState(els.spritePreviewCard3, {
      placeholderText: getCreateVideoT('select_to_view_preview', 'Generate a preview to review it here'),
    });
  } else {
    setPreviewCardState(els.spritePreviewCard3, { imgUrl: imageUrl, showImg: true });
  }

  if (els.spritePreviewStatus3) els.spritePreviewStatus3.textContent = '';

  const hasPreview = !!imageUrl && !loading;
  if (els.discardSpritePreview3) {
    els.discardSpritePreview3.disabled = !hasPreview;
    els.discardSpritePreview3.classList.toggle('hidden', !hasPreview);
  }
  if (els.publishSpritePreview3) {
    els.publishSpritePreview3.disabled = !hasPreview;
    els.publishSpritePreview3.classList.toggle('hidden', !hasPreview);
  }
};

const clearTool3Preview = () => {
  state.tool3.preview.imageUrl = null;
  state.tool3.preview.generatedFor = null;
  setTool3PreviewVisibility(false);
  renderTool3PreviewCard({ imageUrl: '' });
};

const normalizeCommaTags = (value) => {
  if (value == null) return '';
  const raw = Array.isArray(value)
    ? value.map((v) => normalizeCommaTags(v)).filter(Boolean).join(', ')
    : String(value);
  if (!raw) return '';
  return raw
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/,{2,}/g, ',')
    .replace(/^\s*,\s*|\s*,\s*$/g, '')
    .trim();
};

const generateQuickSpritePreview = async ({ prompt, gender, characterId, outfitId }) => {
  if (!prompt) throw new Error('Missing preview prompt');
  const blocked = await getBlockedFilteredImagePromptMessage(prompt);
  if (blocked) throw new Error(blocked);
  const unrecognized = await getBlockedUnrecognizedPromptMessage(prompt);
  if (unrecognized) throw new Error(unrecognized);

  const { response, data } = await postGenerate({
    prompt,
    user_prompt: prompt,
    character_id: characterId || null,
    include_character_appearance: true,
    outfit_id: outfitId === '' ? null : outfitId,
    include_outfit_prompt: true,
    type: 'character',
    parameters: {
      negative_prompt: 'bad quality,worst quality,worst detail,sketch,censor, watermark, text,',
    },
    gender: gender || null,
    nsfwAllowed: getNsfwEnabled(),
  });

  if (!response.ok) {
    const code = data?.details || data?.error;
    const msg = (code && resolveImageGenError(code)) || code || data?.message || 'Preview generation failed';
    throw new Error(msg);
  }

  const imageUrl = data?.imageUrl || '';
  if (!imageUrl) {
    throw new Error('No preview image returned');
  }

  return imageUrl;
};

const handleValidateSpriteRef = async () => {
  const els = getElements();
  const actionIds = getCreatableSelectedActionIds();
  if (actionIds.length === 0) return;

  const token = await getAuthToken();
  if (!token) {
    await showFreeAccountModalIfNeeded();
    return;
  }

  const { charId: receiverCharId, outfitId: receiverOutfitId } = getSpriteRefReceiverIds();
  if (!receiverCharId || !hasOutfitId(receiverOutfitId)) return;

  const isFree = (storage.get('userData')?.current_tier || 'free') === 'free';
  const rawPrompt = els.customOutfitPromptRef?.value?.trim() || '';
  const rawNegative = els.customOutfitNegativeRef?.value?.trim() || '';
  const positivePrompt = (!isFree && rawPrompt) ? rawPrompt : undefined;
  const negativePrompt = (!isFree && rawNegative) ? rawNegative : '';
  const customPromptText = [positivePrompt, negativePrompt].filter(Boolean).join(', ');
  if (customPromptText) {
    const blocked = await getBlockedFilteredImagePromptMessage(customPromptText);
    if (blocked) {
      showError(blocked, { duration: 0, closable: true });
      return;
    }
    const unrecognized = await getBlockedUnrecognizedPromptMessage(customPromptText);
    if (unrecognized) {
      showError(unrecognized, { duration: 0, closable: true });
      return;
    }
  }

  const spritesToCreate = actionIds.map((actionId) => ({ action_id: actionId }));

  els.validateSpriteRef.disabled = true;
  setButtonBusy(els.validateSpriteRef, true);

  try {
    await fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/schedule-sprites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: {
        character_id: receiverCharId,
        outfit_id: receiverOutfitId,
        sprites: spritesToCreate,
        ...(positivePrompt ? { positive_prompt: positivePrompt } : {}),
        ...(!isFree ? { negative_prompt: negativePrompt } : {}),
      },
    });

    showNotification(`${spritesToCreate.length} images scheduled for creation!`, 'success');
    state.tool1.selectedKeys.clear();

    if (spritesToCreate.length > 0) {
      const firstSprite = spritesToCreate[0];
      await noteSpriteImageColdStart({
        char_id: receiverCharId,
        outfit_id: receiverOutfitId,
        action_id: firstSprite.action_id,
      });
      await waitForSpriteCompletion(
        receiverCharId,
        receiverOutfitId,
        firstSprite.action_id,
        token,
        () => { updateTool1Grid(); },
        180000,
        (status, data) => notifyJobStatus('image', status, data)
      );
    }
  } catch (error) {
    showNotification(error.message || 'Failed to create images', 'error');
  } finally {
    updateTool1Credits();
    setButtonBusy(els.validateSpriteRef, false);
  }
};

const handleAiWandPrompt3 = async () => {
  const els = getElements();
  if (els.customImageFlow3 && !state.tool3.customFlowEnabled) return;

  const charId = els.receiverChar3.value;
  const outfitId = els.receiverOutfit3.value;

  if (!charId || !hasOutfitId(outfitId)) {
    showNotification('Please select a character and outfit first', 'error');
    return;
  }

  const instruction = await openFieldEditModal({ fieldLabel: resolveCreateCommonText('field_edit.field_sprite_prompt') });
  if (!instruction) return;

  const token = await getAuthToken();
  if (!token) {
    showFreeAccountModalIfNeeded();
    return;
  }

  const btn = els.aiWandPrompt3;
  const currentPrompt = els.promptText3?.value?.trim() || '';
  if (btn) {
    btn.disabled = true;
    setButtonBusy(btn, true);
  }

  try {
    const payload = await requestAiGenerateField({
      field: 'spritePrompt',
      instruction: instruction.trim(),
      currentValue: currentPrompt,
    }, token);

    const value = typeof payload?.value === 'string' ? payload.value.trim() : '';
    if (value && els.promptText3) {
      els.promptText3.value = value;
      els.promptText3.dispatchEvent(new Event('input', { bubbles: true }));
    }
    const spriteName = typeof payload?.spriteName === 'string' ? payload.spriteName.trim() : '';
    if (spriteName && els.spriteName3) {
      els.spriteName3.value = spriteName;
      els.spriteName3.dispatchEvent(new Event('input', { bubbles: true }));
    }
  } catch (e) {
    if (e.message === 'QUOTA_HANDLED') return;
    showNotification(e.message || 'AI prompt generation failed', 'error');
  } finally {
    if (btn) {
      setButtonBusy(btn, false);
      btn.disabled = !state.tool3.customFlowEnabled;
    }
  }
};

const handleValidateSpriteText = async () => {
  const els = getElements();
  if (els.customImageFlow3 && !state.tool3.customFlowEnabled) return;

  const charId = els.receiverChar3.value;
  const outfitId = els.receiverOutfit3.value;
  const promptText = els.promptText3?.value?.trim() || '';
  const spriteName = els.spriteName3?.value?.trim() || '';

  if (!charId || !hasOutfitId(outfitId)) {
    showNotification('Please select a character and outfit', 'error');
    return;
  }

  if (!promptText) {
    showNotification('Please enter a prompt', 'error');
    return;
  }

  if (!spriteName) {
    showNotification('Please enter an image name', 'error');
    return;
  }

  els.validateSpriteText.disabled = true;
  setButtonBusy(els.validateSpriteText, true);

  try {
    const gender = resolveGenderForTool(3);
    setTool3PreviewVisibility(true);
    renderTool3PreviewCard({ imageUrl: '', loading: true });

    const previewPrompt = normalizeCommaTags(promptText);

    const imageUrl = await generateQuickSpritePreview({ prompt: previewPrompt, gender, characterId: charId, outfitId });

    state.tool3.preview.imageUrl = imageUrl;
    state.tool3.preview.generatedFor = {
      charId,
      outfitId,
      promptText,
      spriteName,
      gender,
      previewPrompt,
      lora: buildTool3LoraPayload(),
    };

    renderTool3PreviewCard({ imageUrl });
  } catch (error) {
    clearTool3Preview();
    showError(error.message || 'Failed to generate preview', { duration: 0, closable: true });
  } finally {
    els.validateSpriteText.disabled = false;
    setButtonBusy(els.validateSpriteText, false);
  }
};

const handleDiscardTool3Preview = () => {
  clearTool3Preview();
  showNotification('Preview discarded.', 'info');
};

const handlePublishTool3Preview = async () => {
  const els = getElements();
  const preview = state.tool3.preview.generatedFor;
  const imageUrl = state.tool3.preview.imageUrl;

  if (!preview || !imageUrl) {
    showNotification('No preview to publish. Generate a preview first.', 'error');
    return;
  }

  const { charId, outfitId, promptText, spriteName } = preview;
  if (!charId || outfitId === '' || !promptText || !spriteName) {
    showNotification('Preview data is incomplete. Please generate again.', 'error');
    clearTool3Preview();
    return;
  }
  const normalizedSpriteName = formatSpriteNameForPublish(spriteName);
  if (!normalizedSpriteName) {
    showNotification('Preview data is incomplete. Please generate again.', 'error');
    clearTool3Preview();
    return;
  }

  const token = await getAuthToken();
  if (!token) {
    await showFreeAccountModalIfNeeded();
    return;
  }

  if (els.publishSpritePreview3) els.publishSpritePreview3.disabled = true;
  if (els.discardSpritePreview3) els.discardSpritePreview3.disabled = true;
  renderTool3PreviewCard({ imageUrl, loading: true });

  try {
    const blocked = await getBlockedFilteredImagePromptMessage(promptText);
    if (blocked) {
      renderTool3PreviewCard({ imageUrl });
      showError(blocked, { duration: 0, closable: true });
      return;
    }
    const lora = getNsfwEnabled() ? (buildTool3LoraPayload() || preview.lora || null) : null;
    const result = await fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/schedule-sprite-from-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: {
        character_id: charId,
        outfit_id: outfitId,
        action_name: normalizedSpriteName,
        action_prompt: promptText,
        ...(lora ? { lora } : {}),
      },
    });

    const spriteIdentifier = result?.sprite_identifier;

    showNotification('Image scheduled for creation!', 'success');
    clearTool3Preview();
    if (els.promptText3) els.promptText3.value = '';
    if (els.spriteName3) els.spriteName3.value = '';
    if (els.spriteLoraId3) els.spriteLoraId3.value = '';
    if (els.spriteLoraStrength3) els.spriteLoraStrength3.value = '';
    if (els.spriteAdvancedLora3) els.spriteAdvancedLora3.removeAttribute('open');
    validateTool3Form();
    updateTool3Credits();

    if (spriteIdentifier) {
      await noteSpriteImageColdStart(spriteIdentifier);
      await waitForSpriteCompletion(
        spriteIdentifier.char_id,
        spriteIdentifier.outfit_id,
        spriteIdentifier.action_id,
        token,
        () => {
          updateToolPreview(3);
          updateTool1Grid();
        },
        180000,
        (status, data) => notifyJobStatus('image', status, data)
      );
    } else {
      await updateToolPreview(3);
      await updateTool1Grid();
    }
  } catch (error) {
    renderTool3PreviewCard({ imageUrl });
    if (error.status === 409) {
      showNotification('Image already exists', 'error');
    } else {
      showNotification(error.body?.error || error.body?.message || error.message || 'Failed to publish image', 'error');
    }
  } finally {
    if (els.publishSpritePreview3) els.publishSpritePreview3.disabled = !state.tool3.preview.imageUrl;
    if (els.discardSpritePreview3) els.discardSpritePreview3.disabled = !state.tool3.preview.imageUrl;
  }
};

const noteSpriteImageColdStart = async (identifier) => {
  const charId = identifier?.char_id ?? identifier?.character_id;
  const outfitId = identifier?.outfit_id;
  const actionId = identifier?.action_id;
  if (charId == null || outfitId == null || actionId == null) return;
  try {
    const data = await fetchCache.fetchJSON(`${DELAY_ESTIMATION_URL}/estimate-image-duration`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: {
        char_id: charId,
        outfit_id: outfitId,
        action_id: actionId,
      },
      skipCache: true,
    });
    notifyVideoContainerStarting(data, `sprite:${charId}:${outfitId}:${actionId}`, 'image');
  } catch (error) {
    console.warn('[CreateVideo] image container check failed', error);
  }
};

const noteSpriteVideoColdStart = async (identifier) => {
  const charId = identifier?.char_id;
  const outfitId = identifier?.outfit_id;
  const actionId = identifier?.action_id;
  const videoActionId = identifier?.video_action_id;
  if (charId == null || outfitId == null || actionId == null || videoActionId == null) return;
  try {
    const data = await fetchCache.fetchJSON(`${DELAY_ESTIMATION_URL}/estimate-video-duration`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: {
        char_id: charId,
        outfit_id: outfitId,
        action_id: actionId,
        video_action_id: videoActionId,
      },
      skipCache: true,
    });
    notifyVideoContainerStarting(data, `sprite:${charId}:${outfitId}:${actionId}:${videoActionId}`);
  } catch (error) {
    console.warn('[CreateVideo] video container check failed', error);
  }
};

const handleValidateVideoText = async () => {
  const els = getElements();
  const charId = els.receiverChar4.value;
  const outfitRaw = els.receiverOutfit4.value;
  const spriteKey = state.tool4.selectedSpriteKey;
  const promptText = els.cameraText4.value.trim();

  if (!charId || !hasOutfitId(outfitRaw)) {
    showNotification('Please select a character and outfit', 'error');
    return;
  }
  const outfitId = Number(outfitRaw);

  if (!spriteKey) {
    showNotification('Please select a source image', 'error');
    return;
  }

  if (!promptText) {
    showNotification('Please enter a prompt', 'error');
    return;
  }

  const token = await getAuthToken();
  if (!token) {
    await showFreeAccountModalIfNeeded();
    return;
  }

  const actionId = parseInt(spriteKey, 10);
  let lora = null;
  try {
    lora = buildTool4LoraPayload();
  } catch (error) {
    showNotification(error.message || 'Invalid LoRA settings', 'error');
    return;
  }

  els.validateVideoText.disabled = true;
  setButtonBusy(els.validateVideoText, true);

  try {
    const result = await fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/schedule-video-from-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: {
        character_id: charId,
        outfit_id: outfitId,
        action_id: actionId,
        action_text: promptText,
        ...(lora ? { lora } : {}),
      },
    });

    showNotification('Video scheduled for creation!', 'success');
    hideTool4CreateForm();

    if (result.video_identifier) {
      await noteSpriteVideoColdStart(result.video_identifier);
      await waitForVideoCompletion(
        result.video_identifier.char_id,
        result.video_identifier.outfit_id,
        result.video_identifier.action_id,
        token,
        () => { updateTool4SpriteGrid(); },
        300000,
        'lowres',
        (status, data) => notifyJobStatus('video', status, data)
      );
    } else {
      await updateTool4SpriteGrid();
    }
  } catch (error) {
    showNotification(sfwModerationMessage(error) || error.message || 'Failed to create video', 'error');
  } finally {
    els.validateVideoText.disabled = false;
    setButtonBusy(els.validateVideoText, false);
  }
};

const handleValidateVideoUpgrade = async () => {
  const els = getElements();
  const charId = els.receiverChar4.value;
  const outfitRaw = els.receiverOutfit4.value;
  const spriteKeyValue = state.tool4.selectedSpriteKey;

  if (!charId || !hasOutfitId(outfitRaw)) {
    showNotification('Please select a character and outfit', 'error');
    return;
  }
  const outfitId = Number(outfitRaw);
  if (!spriteKeyValue) {
    showNotification('Please select a video to upgrade', 'error');
    return;
  }

  const token = await getAuthToken();
  if (!token) {
    await showFreeAccountModalIfNeeded();
    return;
  }

  const actionId = parseInt(spriteKeyValue, 10);
  const selectedVideo = state.tool4.videos.find((entry) => spriteKey(entry) === spriteKeyValue);

  els.validateVideoUpgrade.disabled = true;
  setButtonBusy(els.validateVideoUpgrade, true);

  try {
    const result = await upgradeVideoResolutionRequest({ token, characterId: charId, outfitId, actionId });
    if (!result.success) {
      throw new Error(result.error || 'Failed to upgrade video');
    }

    showNotification('Video queued for HD upgrade!', 'success');
    await syncSpriteCredits();
    hideTool4CreateForm();

    const identifier = result.data?.video_identifier;
    await noteSpriteVideoColdStart({
      char_id: identifier?.char_id ?? charId,
      outfit_id: identifier?.outfit_id ?? outfitId,
      action_id: identifier?.action_id ?? actionId,
      video_action_id: identifier?.video_action_id ?? selectedVideo?.video_action_id,
    });
    await waitForVideoCompletion(
      identifier?.char_id ?? charId,
      identifier?.outfit_id ?? outfitId,
      identifier?.action_id ?? actionId,
      token,
      () => { updateTool4SpriteGrid(); },
      300000,
      'hires',
      (status, data) => notifyJobStatus('video', status, data)
    );
  } catch (error) {
    showNotification(error.message || 'Failed to upgrade video', 'error');
  } finally {
    els.validateVideoUpgrade.disabled = false;
    setButtonBusy(els.validateVideoUpgrade, false);
  }
};

const bindReceiverPair = ({ tool, onCharChange, onOutfitChange }) => {
  const els = getElements();
  const config = TOOL_PREVIEW_CONFIG[tool];
  const charEl = els[config?.charKey || `receiverChar${tool}`];
  const outfitEl = els[config?.outfitKey || `receiverOutfit${tool}`];
  const outfitSelectId = config?.outfitSelectId || `receiver-outfit-${tool}`;

  charEl?.addEventListener('change', async () => {
    const charId = charEl.value;
    await populateOutfitDropdown(outfitSelectId, charId);
    refreshOutfitSearch(outfitSelectId);
    await onCharChange?.({ charId, outfitEl });
    updateToolArrowVisibility(tool);
  });

  outfitEl?.addEventListener('change', () => {
    onOutfitChange?.();
    updateToolArrowVisibility(tool);
  });
};

const applyCharOutfitPrefill = async ({ tool, char, outfit, pending }) => {
  const els = getElements();
  const config = TOOL_PREVIEW_CONFIG[tool];
  const charEl = els[config?.charKey || `receiverChar${tool}`];
  const outfitEl = els[config?.outfitKey || `receiverOutfit${tool}`];
  const outfitSelectId = config?.outfitSelectId || `receiver-outfit-${tool}`;
  if (!char || !hasOutfitId(outfit) || !charEl) return false;
  if (!state.characters.some((c) => String(c.id) === String(char))) return false;
  pending?.();
  charEl.value = char;
  await populateOutfitDropdown(outfitSelectId, char);
  refreshOutfitSearch(outfitSelectId);
  if (outfitEl) {
    outfitEl.value = outfit;
    outfitEl.dispatchEvent(new Event('change'));
  }
  updateToolArrowVisibility(tool);
  return true;
};

const hydratedModes = { sprite: false, video: false };
let outfitPromptAutocompleteReady = false;
let imagePromptHelpersReady = false;

const ensureOutfitPromptAutocomplete = () => {
  if (outfitPromptAutocompleteReady) return;
  const details = document.getElementById('sprite-custom-prompt-advanced');
  if (!details || details.classList.contains('hidden')) return;
  outfitPromptAutocompleteReady = true;
  initializeOutfitPromptAutocomplete('sprite-custom-prompt-ref')
    .catch((e) => console.warn('init custom outfit prompt autocomplete failed', e));
  initializeNegativePromptAutocomplete('sprite-custom-negative-ref')
    .catch((e) => console.warn('init custom outfit negative autocomplete failed', e));
};

const ensureImagePromptHelpers = () => {
  if (imagePromptHelpersReady) return;
  imagePromptHelpersReady = true;
  initializePromptAutocomplete('prompt-text-3', 'spriteActionPrompt', null, {})
    .catch((e) => console.warn('init prompt autocomplete failed', e));
  initPromptHelpers({ helperRootId: 'prompt-helper-3', pillsRootId: 'prompt-helper-pills-3', textareaId: 'prompt-text-3' });
};

const hydrateImageTool = () => {
  if (hydratedModes.sprite) return;
  hydratedModes.sprite = true;
  populateCharacterDropdown('receiver-char-3');
  initializeSearchDropdowns(MODES.SPRITE_TEXT);
  attachPreviewControls(3);
  ensureImagePromptHelpers();
};

const hydrateVideoTool = ({ defaultChar = false } = {}) => {
  if (hydratedModes.video) return;
  hydratedModes.video = true;
  populateCharacterDropdown('receiver-char-4');
  populateCharacterDropdown('receiver-char-free-video');
  initializeSearchDropdowns(MODES.VIDEO_TEXT);
  attachPreviewControls(4);
  attachPreviewControls('freeVideo');
  if (!defaultChar) return;
  const charEl = getElements().receiverChar4;
  if (charEl && !charEl.value && ensureSelectHasDefault(charEl)) {
    charEl.dispatchEvent(new Event('change'));
  }
};

const bindEventListeners = () => {
  const els = getElements();

  els.modeTabs.forEach((tab) => {
    tab.addEventListener('click', handleModeTabClick);
    tab.addEventListener('keydown', handleModeTabKeydown);
  });

  els.spriteFlowTabs.forEach((tab) => {
    tab.addEventListener('click', handleSpriteFlowTabClick);
    tab.addEventListener('keydown', handleSpriteFlowTabKeydown);
  });

  els.validateSpriteRef?.addEventListener('click', handleValidateSpriteRef);

  spriteNameFilters.ref = bindSpriteGalleryNameFilter(
    els.spriteActionSearchRef,
    els.spriteGridRef,
    '.sprite-comparison-item'
  );
  spriteNameFilters.video = bindSpriteGalleryNameFilter(
    els.spriteActionSearchVideo,
    els.spriteGridForVideo,
    '.sprite-comparison-item'
  );

  bindReceiverPair({
    tool: 3,
    onCharChange: async ({ outfitEl }) => {
      clearTool3Preview();
      state.tool1.selectedKeys.clear();
      state.tool1.forcedKeys.clear();
      validateTool3Form();
      ensureSelectHasDefault(outfitEl);
      if (hasOutfitId(outfitEl?.value)) {
        outfitEl.dispatchEvent(new Event('change'));
      } else {
        await updateTool1Grid();
        await updateToolPreview(3);
      }
      updateTool3Credits();
    },
    onOutfitChange: () => {
      clearTool3Preview();
      state.tool1.selectedKeys.clear();
      state.tool1.forcedKeys.clear();
      updateToolPreview(3);
      validateTool3Form();
      updateTool1Grid();
      ensureOutfitPromptAutocomplete();
    },
  });

  els.validateSpriteText?.addEventListener('click', handleValidateSpriteText);
  els.aiWandPrompt3?.addEventListener('click', handleAiWandPrompt3);
  els.aiWandPrompt3?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAiWandPrompt3(); }
  });
  els.promptText3?.addEventListener('input', () => {
    if (state.tool3.preview.imageUrl) clearTool3Preview();
    validateTool3Form();
  });
  els.spriteName3?.addEventListener('input', () => {
    if (state.tool3.preview.imageUrl) clearTool3Preview();
    validateTool3Form();
  });
  [els.spriteLoraId3, els.spriteLoraStrength3].forEach((inputEl) => {
    inputEl?.addEventListener('input', () => {
      validateTool3Form();
      updateTool3Credits();
    });
  });
  els.discardSpritePreview3?.addEventListener('click', handleDiscardTool3Preview);
  els.discardSpritePreview3?.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    handleDiscardTool3Preview();
  });
  els.publishSpritePreview3?.addEventListener('click', handlePublishTool3Preview);
  els.publishSpritePreview3?.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    handlePublishTool3Preview();
  });

  bindReceiverPair({
    tool: 4,
    onCharChange: async ({ outfitEl }) => {
      hideTool4CreateForm();
      ensureSelectHasDefault(outfitEl);
      if (hasOutfitId(outfitEl?.value)) {
        outfitEl.dispatchEvent(new Event('change'));
      } else {
        await updateToolPreview(4);
      }
    },
    onOutfitChange: () => {
      hideTool4CreateForm();
      updateTool4SpriteGrid();
    },
  });

  bindReceiverPair({
    tool: 'freeVideo',
    onCharChange: async ({ outfitEl }) => {
      ensureSelectHasDefault(outfitEl);
      if (hasOutfitId(outfitEl?.value)) {
        outfitEl.dispatchEvent(new Event('change'));
      } else {
        await updateToolPreview('freeVideo');
      }
    },
    onOutfitChange: () => {
      updateToolPreview('freeVideo');
    },
  });

  els.cameraText4?.addEventListener('input', validateTool4Form);
  spriteVideoLoras = mountVideoLoraRows({
    rowsEl: els.videoLoraRows,
    addButton: els.videoAddLora,
    titleEl: els.videoLoraTitle,
    max: SPRITE_VIDEO_LORA_MAX,
    onChange: updateTool4Credits,
    text: getCreateVideoT,
  });
  els.validateVideoText?.addEventListener('click', handleValidateVideoText);
  els.validateVideoUpgrade?.addEventListener('click', handleValidateVideoUpgrade);
};

const setupNotificationPolling = () => {
  pollCreateVideoJobs({
    getActiveMode: () => state.mode,
    refreshImageGrid: updateTool1Grid,
    refreshVideoGrid: updateTool4SpriteGrid,
    shouldSuppressToast: shouldSuppressJobToast,
    onToast: showNotification,
  });
};

export const bootCreateVideoPage = async (config) => {
  await syncSpriteCredits();

  await Promise.all([
    loadCreditPricing(),
    loadCharacters(),
    loadOutfits(),
    loadMappings(),
  ]);

  bindEventListeners();
  bindPreviewCardContextMenus();
  attachReceiverSwipeControls();
  initFreeImageFlow({
    initialKind: config?.mode === MODES.VIDEO_TEXT
      ? IMAGE_KINDS.SPRITE
      : (config?.imageKind || IMAGE_KINDS.SPRITE),
    getCharacters: () => state.characters,
  });
  initFreeVideoFlow({
    initialKind: config?.videoKind || VIDEO_KINDS.SPRITE,
    initialSource: config?.videoSource,
    outputKey: config?.outputKey,
    getCharacters: () => state.characters,
  });
  initSfwLoraLock();

  const initialMode = config?.mode || MODES.SPRITE_TEXT;
  const branch = config?.char && hasOutfitId(config.outfit)
    ? (initialMode === MODES.VIDEO_TEXT ? 'video-prefill' : 'image-prefill')
    : 'default-character';
  console.info('[CreateVideo][boot]', {
    entrypoint: 'create-video',
    mode: initialMode,
    char: config?.char || null,
    outfit: config?.outfit ?? null,
    action: config?.action || null,
    intent: config?.intent || null,
    branch,
  });

  setActiveMode(initialMode);
  setSpriteFlow(SPRITE_FLOWS.OUTFIT);
  const els = getElements();

  if (initialMode === MODES.VIDEO_TEXT) {
    hydrateVideoTool();
    const prefilled = await applyCharOutfitPrefill({
      tool: 4,
      char: config.char,
      outfit: config.outfit,
      pending: () => {
        if (config.action && (config.intent === 'upgrade' || config.intent === 'create')) {
          state.tool4.pendingSelectActionId = String(parseInt(config.action, 10));
          state.tool4.pendingSelectIntent = config.intent;
          state.tool4.pendingSelectCharId = String(config.char);
          state.tool4.pendingSelectOutfitId = String(config.outfit);
        }
      },
    });
    if (!prefilled && ensureSelectHasDefault(els.receiverChar4)) {
      els.receiverChar4.dispatchEvent(new Event('change'));
    }
  } else {
    hydrateImageTool();
    const prefilled = await applyCharOutfitPrefill({
      tool: 3,
      char: config.char,
      outfit: config.outfit,
    });
    if (!prefilled && ensureSelectHasDefault(els.receiverChar3)) {
      els.receiverChar3.dispatchEvent(new Event('change'));
    }
    updateTool3Credits();
    clearTool3Preview();
  }

  updateToolArrowVisibility(3);
  updateToolArrowVisibility(4);

  document.addEventListener('spriteCreditsUpdated', () => {
    updateTool1Credits();
    updateTool3Credits();
    updateTool4Credits();
    updateTool4UpgradeCredits();
  });

  setupNotificationPolling();
};

